import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  Fragment,
} from 'react'
import PropTypes from 'prop-types'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { rgba } from 'polished'
import { isEmpty, kebabCase, toLower } from 'lodash'
import { useTheme } from '@emotion/react'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import { graphql, useStaticQuery } from 'gatsby'
import {
  Button,
  Tooltip,
  List,
  ListItem,
  Container,
} from 'chaoskit/src/components'
import { link, text } from 'chaoskit/src/assets/styles/utility'

import Foundation from './Foundation'

import Icon from '~components/Icon'
import Link from '~components/Link'
import { ReactComponent as LogoSvg } from '~media/global/wordmark.svg'
import DocSearch from '~partials/docs/DocSearch'
import HelmetSEO from '~partials/global/HelmetSEO'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import { StylesDocsLayout } from '~partials/docs/UI'
import TableOfContents from '~components/TableOfContents'

const SectionHeader = ({ as: Component = 'div', ...rest }) => {
  const theme = useTheme()

  return (
    <Component
      css={{
        ...text.expanded(theme),
        ...theme.text.xsmall,
        fontWeight: theme.fontWeight.bold,
      }}
      {...rest}
    />
  )
}

SectionHeader.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}

const MenuListItem = ({ menuSection, handleNavClick }) => {
  const theme = useTheme()

  return (
    <div>
      <SectionHeader
        as={menuSection.hasOverview ? TextLink : undefined}
        href={
          menuSection.hasOverview
            ? `/docs/${kebabCase(toLower(menuSection.title))}/overview`
            : undefined
        }
        css={[
          {
            opacity: theme.opacity.base,
            color: 'inherit',
          },

          menuSection.hasOverview && {
            '&:hover, &:focus, &[aria-current="page"]': {
              opacity: 1,
              color: theme.color.primary.base,
            },
          },

          !menuSection.hasOverview && {
            cursor: 'default',
          },
        ]}
      >
        {menuSection.title}
      </SectionHeader>

      <List
        space="xsmall"
        css={{ marginBottom: 0, marginTop: theme.space.small }}
      >
        {menuSection.items.map((item) => (
          <ListItem key={item.fileAbsolutePath}>
            <TextLink
              className="u-link"
              href={item.fields.fullUrl}
              onClick={handleNavClick}
            >
              {item.frontmatter.title}
            </TextLink>
          </ListItem>
        ))}
      </List>
    </div>
  )
}

MenuListItem.propTypes = {
  menuSection: PropTypes.object.isRequired,
  handleNavClick: PropTypes.func.isRequired,
}

const SideNavigation = ({ isMobileNavOpen, setIsMobileNavOpen, menuItems }) => {
  const theme = useTheme()

  const navRef = useRef()

  useEffect(() => {
    const scrollPosition = window.localStorage.getItem(
      'gremlin__docs__navScroll'
    )

    if (scrollPosition) {
      navRef.current.scrollTop = scrollPosition
    }
  }, [])

  useUpdateEffect(() => {
    if (isMobileNavOpen) {
      // We're not using a sticky header here, so this takes care of sticking header to the top of the viewport if the user scrolls down "juuuust a bit" and clicks the mobile toggle
      window.scrollTo(0, 0)

      disableBodyScroll(navRef.current)
    } else {
      enableBodyScroll(navRef.current)
    }
  }, [isMobileNavOpen])

  // Make sure we close mobile nav if resizing from mobile to desktop
  const checkSize = useCallback(
    (event) => {
      if (event.matches) {
        setIsMobileNavOpen(false)
      }
    },
    [setIsMobileNavOpen]
  )

  useEffect(() => {
    const viewportMediumMin = window.matchMedia(
      `(min-width: ${theme.breakpoint.medium}px)`
    )

    viewportMediumMin.addListener(checkSize)

    return () => {
      viewportMediumMin.removeListener(checkSize)
    }
  }, [theme.breakpoint.medium, checkSize])

  // Make sure we kill mobile nav styles between routes
  const handleNavClick = () => {
    window.localStorage.setItem(
      'gremlin__docs__navScroll',
      navRef.current.scrollTop
    )

    if (isMobileNavOpen) {
      setIsMobileNavOpen(false)
    }
  }

  return (
    <aside
      ref={navRef}
      css={{
        background: theme.brand.darkViolet,
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100%',
        overflow: 'auto',
        width: StylesDocsLayout(theme).nav.width,
        zIndex: 10,
        overscrollBehavior: 'contain',

        [theme.mq.small__max]: [
          {
            width: '100%',
            display: 'none',
          },

          isMobileNavOpen && {
            top: StylesDocsLayout(theme).header.height,
            height: `calc(100% - ${StylesDocsLayout(theme).header.height}px)`,
            display: 'block',
          },
        ],
      }}
      className="u-contrast"
    >
      {!isMobileNavOpen && (
        <header
          css={{
            display: 'flex',
            alignItems: 'center',
            height: StylesDocsLayout(theme).header.height,
            paddingLeft: StylesDocsLayout(theme).nav.space,
            paddingRight: StylesDocsLayout(theme).nav.space,
            flex: 'none',
          }}
        >
          <Link
            onClick={handleNavClick}
            to="/docs/"
            css={{
              display: 'inline-grid',
              gridAutoFlow: 'column',
              gridAutoColumn: 'auto',
              justifyContent: 'start',
              alignItems: 'normal',
              gap: theme.space.small,
              color: theme.contrast.base,
              lineHeight: 1,
            }}
          >
            <LogoSvg
              css={{
                height: theme.fontSize.medium,
                color: theme.contrast.base,
              }}
            />
            <span
              css={{
                ...theme.text.large,
                fontWeight: theme.fontWeight.bold,
                color: theme.brand.lightViolet,
              }}
            >
              Docs
            </span>
          </Link>
        </header>
      )}
      <nav
        css={{
          marginTop: theme.space.large,
          marginBottom: theme.space.large,
          paddingLeft: StylesDocsLayout(theme).nav.space,
          paddingRight: StylesDocsLayout(theme).nav.space,
        }}
      >
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: theme.space.medium,
          }}
        >
          {menuItems.map((menuSection) => {
            // If the section returned doesn't have any items, don't show
            if (!menuSection.items.length) return

            return (
              <MenuListItem
                key={menuSection.fileAbsolutePath}
                menuSection={menuSection}
                handleNavClick={handleNavClick}
              />
            )
          })}
        </div>
      </nav>
      <div
        css={{
          background: theme.color.primary.base,
          padding: theme.space.medium,
          boxShadow: `0 -10px 20px ${rgba(
            theme.color.dark.base,
            theme.opacity.base
          )},
                inset 0 1px 0 ${rgba(theme.contrast.base, 0.15)}`,
          marginTop: 'auto',
          textAlign: 'center',
        }}
      >
        <div
          css={{
            ...theme.text.medium,
            fontWeight: theme.fontWeight.bold,
            marginBottom: theme.space.base,
          }}
        >
          Start your 30 day free trial.
        </div>
        <ButtonLink url="/trial/?ref=docs" type="secondary" size="xsmall">
          Start for free
        </ButtonLink>
      </div>
    </aside>
  )
}

SideNavigation.propTypes = {
  isMobileNavOpen: PropTypes.bool,
  menuItems: PropTypes.array.isRequired,
  setIsMobileNavOpen: PropTypes.func.isRequired,
}

const DocsHeader = ({ isMobileNavOpen, setIsMobileNavOpen }) => {
  const theme = useTheme()

  return (
    <header
      css={{
        background: theme.color.panel.base,
        borderBottom: theme.border.base,
      }}
    >
      <Container
        size="large"
        css={{
          marginRight: 0,
          marginLeft: 0,
        }}
      >
        <div
          css={{
            height: StylesDocsLayout(theme).header.height,
            display: 'flex',
            alignItems: 'center',
            flex: 'none',
          }}
        >
          <Link
            to="/docs/"
            css={{
              display: 'inline-block',

              [theme.mq.medium]: {
                display: 'none',
              },
            }}
          >
            <LogoSvg style={{ height: theme.height.xsmall }} />
          </Link>
          <DocSearch />
          <Button
            type="reset"
            onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
            css={{
              color: theme.fontColor.bse,
              marginLeft: 'auto',

              [theme.mq.medium]: {
                display: 'none',
              },
            }}
          >
            {isMobileNavOpen ? (
              <Icon size="large" icon="close" />
            ) : (
              <Icon size="large" icon="menu" />
            )}
          </Button>
          <ButtonLink
            css={{
              marginLeft: 'auto',

              [theme.mq.small__max]: {
                display: 'none',
              },
            }}
            size="xsmall"
            type="default"
            url="https://app.gremlin.com"
          >
            Dashboard
          </ButtonLink>
        </div>
      </Container>
    </header>
  )
}

DocsHeader.propTypes = {
  isMobileNavOpen: PropTypes.bool,
  setIsMobileNavOpen: PropTypes.func.isRequired,
}

const DocsFooter = ({ menuItems, article }) => {
  const theme = useTheme()

  // Get current section (and menu items)
  const currentSection = menuItems.find(
    (menuSection) => menuSection.title === article.frontmatter.section
  )

  // This is useful for the main "Overview" page which we won't appear in the menu array
  if (!currentSection) return false

  // Don't show on release notes
  if (
    article.frontmatter.section === 'Release Notes' ||
    article.frontmatter.platform
  )
    return false

  // Find where this article appears in the list of items
  const articleNavigationMatchIndex = currentSection.items.findIndex(
    (item) => item.fileAbsolutePath === article.fileAbsolutePath
  )

  const previousArticle = currentSection.items[articleNavigationMatchIndex - 1]
  const nextArticle = currentSection.items[articleNavigationMatchIndex + 1]

  // If no navigation items are found, don't render footer
  if (!previousArticle && !nextArticle) return false

  return (
    <footer
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.space.xlarge,
        paddingBottom: theme.space.base,
      }}
    >
      {previousArticle && (
        <Tooltip placement="right" content={previousArticle.frontmatter.title}>
          <ButtonLink
            url={previousArticle.fields.fullUrl}
            size="small"
            type="default"
          >
            Previous
          </ButtonLink>
        </Tooltip>
      )}
      {nextArticle && (
        <Tooltip placement="left" content={nextArticle.frontmatter.title}>
          <ButtonLink
            css={{
              '&:only-child': {
                marginLeft: 'auto',
              },
            }}
            url={nextArticle.fields.fullUrl}
            size="small"
            type="default"
          >
            Next
          </ButtonLink>
        </Tooltip>
      )}
    </footer>
  )
}

DocsFooter.propTypes = {
  article: PropTypes.object.isRequired,
  menuItems: PropTypes.array.isRequired,
}

const DocsLayout = ({ article, nodeType, children, breadcrumbs }) => {
  const theme = useTheme()

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)

  const {
    sections: { edges: sections },
    articles: { edges: articles },
    navigation: {
      childMdx: {
        frontmatter: { navigation },
      },
    },
  } = useStaticQuery(graphql`
    query DocsLayoutPageData {
      sections: allMdx(
        filter: {
          fileAbsolutePath: { regex: "/src/docs/sections/" }
          frontmatter: {
            title: { regex: "/^((?!Overview|BLUEPRINT).)*$/" } # Hide Overview section from navigation
          }
        }
      ) {
        edges {
          node {
            fileAbsolutePath
            frontmatter {
              title
              hasOverview
            }
          }
        }
      }

      articles: allMdx(
        sort: { fields: [frontmatter___title], order: ASC }
        filter: {
          fileAbsolutePath: { regex: "/src/docs/articles/" }
          frontmatter: { title: { ne: "BLUEPRINT" } }
        }
      ) {
        edges {
          node {
            ...DocsArticlesTeaserFragment # Using a smaller payload here to avoid page bloat
          }
        }
      }

      navigation: file(
        name: { eq: "docs__navigation" }
        absolutePath: { regex: "/src/data/" }
      ) {
        childMdx {
          frontmatter {
            navigation {
              section
              items {
                title
                url
              }
            }
          }
        }
      }
    }
  `)

  const menuItems = useMemo(() => {
    // Start "cleaner" menu array that we can map through later
    const menu = []

    // Find relevant sections
    navigation.forEach((pocket, index) => {
      const matchedSection = sections.find(
        ({ node }) => node.frontmatter.title === pocket.section
      )

      if (matchedSection) {
        menu.push({
          ...matchedSection.node.frontmatter,
          fileAbsolutePath: matchedSection.node.fileAbsolutePath,
          items: [],
        })

        // Run through articles and match up with actual data
        pocket.items.forEach((navItem) => {
          let matchedArticle

          // First attempt to find matching internal node
          matchedArticle = articles.find(
            ({ node }) =>
              node.frontmatter.title === navItem.title &&
              node.frontmatter.section === pocket.section
          )

          // Then try to look for URL field for external pages
          if (!matchedArticle && navItem.url) {
            // Stubbing out needed data structure from normal article so we can re-use the same logic
            matchedArticle = {
              node: {
                fileAbsolutePath: navItem.url,
                frontmatter: {
                  title: navItem.title,
                  section: pocket.section,
                },
                fields: {
                  fullUrl: navItem.url,
                },
              },
            }
          }

          if (matchedArticle) {
            menu[index].items.push({
              ...matchedArticle.node,
            })
          }
        })
      }
    })

    return menu
  }, [articles, sections, navigation])

  const getPageTitle = () => {
    // Normal doc article
    if (article.frontmatter.section) {
      // Get around duplicated section/article titles
      if (article.frontmatter.section !== article.frontmatter.title) {
        return `${article.frontmatter.section} > ${article.frontmatter.title}`
      }
      return article.frontmatter.title
    }
    if (article.frontmatter.platform) {
      // Releases node type
      return `${article.frontmatter.platform} ${article.frontmatter.title}`
    }

    // Fallback
    return article.frontmatter.title
  }

  const hasTableOfContents =
    !isEmpty(article.tableOfContents) &&
    article.frontmatter.section !== 'Release Notes'

  return (
    <Foundation>
      <HelmetSEO
        title={getPageTitle()}
        titleTemplate="%s | Gremlin Docs"
        description={article.excerpt}
        noIndex={article.frontmatter?.hideFromResources}
        type="article"
      />
      <NodeTypeTracker nodeType={nodeType} />
      <div css={{ position: 'relative' }}>
        <SideNavigation
          menuItems={menuItems}
          isMobileNavOpen={isMobileNavOpen}
          setIsMobileNavOpen={setIsMobileNavOpen}
        />
        <main
          css={{
            [theme.mq.medium]: {
              marginLeft: StylesDocsLayout(theme).nav.width,
            },
          }}
        >
          <DocsHeader
            isMobileNavOpen={isMobileNavOpen}
            setIsMobileNavOpen={setIsMobileNavOpen}
          />
          <div
            css={{
              // 1. Since this is wrapped around the base `.container` component, the horizontal margins take that into account for overall spacing
              marginTop: theme.space.large,

              // 1
              [theme.mq.medium]: {
                marginLeft: theme.space.base,
                marginRight: theme.space.base,
              },
            }}
          >
            <Container
              size="large"
              css={{
                marginLeft: 0,
                marginRight: 0,

                [theme.mq.medium]: {
                  paddingRight: 0,
                },
              }}
            >
              <article
                css={{
                  h1: {
                    ...theme.text['3xlarge__fluid'],
                  },
                  h3: {
                    ...theme.text['2xlarge__fluid'],
                  },
                  h4: {
                    ...theme.text.xlarge__fluid,
                  },
                  h5: {
                    ...theme.text.large__fluid,
                    fontSize: '22px !important',
                  },
                  h6: {
                    ...theme.text.medium__fluid,
                  },
                }}
              >
                {article.frontmatter?.section !== 'Overview' && (
                  <div
                    css={{
                      display: 'grid',
                      gridAutoColumns: 'auto',
                      gridAutoFlow: 'column',
                      gap: theme.space.small,
                      justifyContent: 'start',
                      alignItems: 'center',
                    }}
                  >
                    <SectionHeader css={{ color: theme.fontColor.muted }}>
                      {article.frontmatter.section}
                    </SectionHeader>
                    {breadcrumbs &&
                      breadcrumbs.map((breadcrumb) => (
                        <Fragment key={breadcrumb.label}>
                          <Icon
                            size="small"
                            icon="caret-right"
                            css={{ top: 0, color: theme.fontColor.muted }}
                          />
                          <SectionHeader
                            as={TextLink}
                            css={[link.muted(theme), link.linkUnderline]}
                            href={breadcrumb.fullUrl}
                          >
                            {breadcrumb.label}
                          </SectionHeader>
                        </Fragment>
                      ))}
                  </div>
                )}
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.space.base,

                    [theme.mq.large]: {
                      marginBottom: theme.space.large,
                    },
                  }}
                >
                  <h1
                    css={{
                      ...theme.headingPreset.h2,
                      margin: 0,
                    }}
                  >
                    {article.frontmatter.title}
                  </h1>
                  {article.frontmatter.section === 'Release Notes' && (
                    <Button
                      iconOnly
                      size="xsmall"
                      type="secondary"
                      url={`/docs/release-notes/${kebabCase(
                        toLower(article.frontmatter.title)
                      )}/rss.xml`}
                      title="Subscribe to updates"
                      css={{ marginLeft: theme.space.base }}
                    >
                      <Icon icon="rss" />
                    </Button>
                  )}
                </div>

                <div
                  css={[
                    hasTableOfContents && {
                      [theme.mq.xlarge]: {
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 250px',
                        gap: theme.space.large,
                      },
                    },
                  ]}
                >
                  {hasTableOfContents && (
                    <div css={{ order: 1 }}>
                      <TableOfContents
                        tree={article.tableOfContents}
                        hasStickyHeader={false}
                        stickyBp="xlarge"
                      />
                    </div>
                  )}

                  <div>{children}</div>
                </div>

                <DocsFooter menuItems={menuItems} article={article} />
              </article>
            </Container>
          </div>
        </main>
      </div>
    </Foundation>
  )
}

DocsLayout.propTypes = {
  children: PropTypes.node,
  article: PropTypes.object.isRequired,
  nodeType: PropTypes.string.isRequired,
  breadcrumbs: PropTypes.array,
}

export default DocsLayout
